// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
    DialogTitle,
} from '@fluentui/react-components';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { WarningImage } from '../../icons/status';
import { useAppStyles } from '../../styles';
import { TrackContext } from '../common';
import { CancelButton, ConfirmButton, ConfirmationField } from '../dialogs';
import CloseButton from '../dialogs/close-button';

// Delete activity confirmation dialog properties.
export type DeleteActivityDialogProps = Readonly<
    Partial<DialogProps> & {
        title: string;
        onDelete: () => void;
    }
>;

// Delete activity confirmation dialog.
export default (props: DeleteActivityDialogProps) => {
    const { title, onDelete, open } = props;
    const [confirmed, setConfirmed] = useState(false);
    const styles = useAppStyles();

    return (
        <TrackContext.Provider value={{ container: 'delete-activity-dialog' }}>
            <Dialog {...props}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle action={<CloseButton />}>
                            <FormattedMessage
                                description="Delete activity dialog title."
                                defaultMessage="Delete activity"
                            />
                        </DialogTitle>
                        <DialogContent className={styles.flexRowCentered}>
                            <WarningImage className={styles.dialogImage} />
                            <div className={styles.dialogContent}>
                                <div>
                                    <FormattedMessage
                                        description="Confirmation text on delete activity dialog."
                                        defaultMessage="Are you sure you want to delete activity ''{ACTIVITY_TITLE}''?"
                                        values={{ ACTIVITY_TITLE: title }}
                                    />
                                </div>
                                <div>
                                    <FormattedMessage
                                        description="Title for a list of items that will be lost when activity is deleted."
                                        defaultMessage="You will permanently lose:"
                                    />
                                    <ul>
                                        <li>
                                            <FormattedMessage
                                                description="An item in the list of things that will be lost when deleting a activity."
                                                defaultMessage="Published content and other files within the activity"
                                            />
                                        </li>
                                        <li>
                                            <FormattedMessage
                                                description="An item in the list of things that will be lost when deleting a activity."
                                                defaultMessage="All student progress associated with the activity"
                                            />
                                        </li>
                                    </ul>
                                    <div className={styles.textSemibold}>
                                        <FormattedMessage
                                            description="Bolded warning text on the delete activity dialog."
                                            defaultMessage="Once deleted, your activity cannot be recovered!"
                                        />
                                    </div>
                                    <br />
                                    <ConfirmationField
                                        dialogOpen={open}
                                        onChange={setConfirmed}
                                        confirmationText="DELETE"
                                    />
                                </div>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <CancelButton />
                            <ConfirmButton className={styles.buttonDanger} onClick={onDelete} disabled={!confirmed}>
                                <FormattedMessage
                                    description="Delete activity dialog confirmation button text."
                                    defaultMessage="Delete activity"
                                />
                            </ConfirmButton>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </TrackContext.Provider>
    );
};
